/**
 * Service to map v4 fields to existing objects
 */
import type { AFPAppointment, CounselorAppointment } from '@/apiTypes'
import { AppointmentRegardingType } from '@/enums/AppointmentRegardingType'
import { LeadSource } from '@/enums/LeadSource'
import { MeetingLocation } from '@/enums/MeetingLocation'
import { ObjectState } from '@/enums/ObjectState'
import { TaskResult } from '@/enums/TaskResult'

export const mapV4FieldsToCounselorAppointmentResult = (
	appt: AFPAppointment
): CounselorAppointment => {
	const opportunity = appt?.opportunity?.data ?? {}
	const ocr = opportunity?.opportunityContactRoles?.data ?? []
	const contact = ocr[0]?.contact?.data ?? {}
	const mailingAddress = contact?.mailingAddress ?? null
	const cepEvent = appt?.cepEvent?.data ?? {}

	return {
		id: appt?.id,
		startAt: appt?.startDateTime,
		endAt: appt?.endDateTime,
		address: appt?.address,
		city: appt?.city,
		state: appt?.state,
		zip: appt?.zip,
		meetingLocationId: getMeetingLocationId(appt?.meetingLocation),
		resultId: getResultId(appt.result),
		objectStateId: getObjectStateId(appt.appointmentStatus),
		regardingTypeId: getRegardingTypeId(appt.subType),
		funeralHome: null, // todo in PSS-1665 / PSS-1666
		opportunityId: opportunity?.id,
		subject: appt?.subject,
		lead: {
			data: {
				id: contact?.fkMscrmId ?? null, // at some point new appointments will not have fkMscrmId
				email: contact?.email,
				firstname: contact?.firstName,
				lastname: contact?.lastName,
				address: mailingAddress?.street,
				city: mailingAddress?.city,
				state: mailingAddress?.state,
				zip: mailingAddress?.postalCode,
				leadSourceId: getLeadSourceId(opportunity?.leadSource),
				primaryPhone: contact?.phone,
				funeralHome: null, // todo in PSS-1665 / PSS-1666
			},
		},
		contactRoles: ocr,
		campaignManager: {
			data: {
				id: cepEvent?.id,
				name: cepEvent?.name,
			},
		},
		event: {
			data: {
				id: cepEvent?.id,
				locationName: cepEvent?.eventLocationName,
				cepEventType: cepEvent?.cepType,
			},
		},
	}
}

const getLeadSourceId = (leadSource: string): LeadSource => {
	switch (leadSource) {
		case 'ACEP':
			return LeadSource.ACEP
		case 'After Care':
			return LeadSource.AFTER_CARE
		case 'Cemetery':
			return LeadSource.CEMETERY
		case 'Cemetery Post Service':
			return LeadSource.CEMETERY_POST_SERVICE
		case 'Cemetery Stepping Stones':
			return LeadSource.CEMETERY_STEPPING_STONES
		case 'CEP':
			return LeadSource.CEP
		case 'Church':
			return LeadSource.CHURCH
		case 'Cremation':
			return LeadSource.CREMATION
		case 'Direct Mail':
			return LeadSource.DIRECT_MAIL
		case 'Direct Mail - FH':
			return LeadSource.DIRECT_MAIL_FH
		case 'Direct Mail Phone Append':
			return LeadSource.DIRECT_MAIL_PHONE_APPEND
		case 'Fair':
			return LeadSource.FAIR
		case 'Forest Lawn':
			return LeadSource.FOREST_LAWN
		case 'Insurance eLeads':
			return LeadSource.INSURANCE_ELEADS
		case 'Internet':
			return LeadSource.INTERNET
		case 'Internet - FH':
			return LeadSource.INTERNET_FH
		case 'Internet - VMP':
			return LeadSource.INTERNET_VMP
		case 'LTFS':
			return LeadSource.LTFS
		case 'Newspaper Insert':
			return LeadSource.NEWSPAPER_INSERT
		case 'Newspaper VMP':
			return LeadSource.NEWSPAPER_VMP
		case 'Other':
			return LeadSource.OTHER
		case 'Other - FH':
			return LeadSource.OTHER_FH
		case 'Personal Referral':
			return LeadSource.PERSONAL_REFERRAL
		case 'Pet - After Care':
			return LeadSource.PET_AFTER_CARE
		case 'Post Service':
			return LeadSource.POST_SERVICE
		case 'Referral':
			return LeadSource.REFERRAL
		case 'Register Book':
			return LeadSource.REGISTER_BOOK
		case 'Seminar':
			return LeadSource.SEMINAR
		case 'Stepping Stones':
			return LeadSource.STEPPING_STONES
		case 'Targeted Direct Mail':
			return LeadSource.TARGETED_DIRECT_MAIL
		case 'Unfunded':
			return LeadSource.UNFUNDED
		case 'Veterans Memorial Program':
			return LeadSource.VETERANS_MEMORIAL_PROGRAM
		case 'Visa Preneed Request':
			return LeadSource.VISA_PRENEED_REQUEST
		case 'Walk-in | Call-in':
			return LeadSource.WALK_IN_CALL_IN
		// Adding these new enums that do not map to CRM
		case 'Altogether Digital':
			return LeadSource.ALTOGETHER_DIGITAL
		case 'Altogether Mail':
			return LeadSource.ALTOGETHER_MAIL
		case 'Cost Estimator Referral':
			return LeadSource.COST_ESTIMATOR_REFERRAL
		case 'EA Core/Connect':
			return LeadSource.EA_CORE_CONNECT
		case 'EverythingAfter+':
			return LeadSource.EVERYTHING_AFTER_PLUS
		case 'VMP Phone Append':
			return LeadSource.VMP_PHONE_APPEND
		default:
			return LeadSource.OTHER
	}
}

const getResultId = (result: string): TaskResult | null => {
	switch (result) {
		case 'Fund':
			return TaskResult.FUND
		case 'Has Plan':
			return TaskResult.HAS_PLAN
		case 'Pending':
			return TaskResult.PENDING
		case 'AFP Follow-up':
			return TaskResult.AFP_FOLLOWUP
		case 'Appointment':
			return TaskResult.NEW_APPOINTMENT
		case 'Unfunded':
			return TaskResult.UNFUNDED
		case 'Not Interested':
			return TaskResult.NOT_INTERESTED
		case 'Do Not Call':
			return TaskResult.NOT_INTERESTED_NO_CALL
		case 'FPC Follow-up':
			return TaskResult.FPC_FOLLOW_UP
		case 'Reschedule':
			return TaskResult.RESCHEDULE
		case 'Do Not Contact':
			return TaskResult.DO_NOT_CONTACT
		case 'Send Info':
			return TaskResult.SEND_INFO
		case 'CEP Group Appt':
			return TaskResult.CEP_GROUP_APPT
		case 'CEP Discovery Appt':
			return TaskResult.CEP_DISCOVERY_APPT
		case 'CEP Event Completed':
			return TaskResult.CEP_EVENT_RESULTED
		default:
			return null
	}
}

const getRegardingTypeId = (subType: string): AppointmentRegardingType => {
	switch (subType) {
		case 'Preneed':
			return AppointmentRegardingType.LEAD
		case 'Discovery':
			return AppointmentRegardingType.DISCOVERY
		case 'CEP Event':
			return AppointmentRegardingType.ACEP_OR_TCEP
		default:
			return AppointmentRegardingType.LEAD
	}
}

const getMeetingLocationId = (meetingLocation: string): MeetingLocation => {
	switch (meetingLocation) {
		case 'Client Home':
			return MeetingLocation.CLIENT_HOME
		case 'Funeral Home':
			return MeetingLocation.FUNERAL_HOME
		case 'Other':
			return MeetingLocation.OTHER
		case 'Phone Consultation':
			return MeetingLocation.PHONE
		default:
			return MeetingLocation.OTHER
	}
}

const getObjectStateId = (appointmentStatus: string): ObjectState | null => {
	switch (appointmentStatus) {
		case 'Open':
			return ObjectState.Open
		case 'Completed':
			return ObjectState.Closed
		case 'Cancelled':
			return ObjectState.Canceled
		default:
			return null
	}
}
